import { Repeat } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

import type { ActivityDetail } from '../api'
import { canEditActivity } from '../access'
import { ConfirmDialog } from './confirm-dialog'

interface CreatorActionsProps {
  activity: ActivityDetail
  cancelBusy: boolean
  stopBusy: boolean
  onCancelActivity: () => Promise<boolean>
  onStopRecurrence: () => Promise<boolean>
}

export function CreatorActions({
  activity,
  cancelBusy,
  stopBusy,
  onCancelActivity,
  onStopRecurrence,
}: CreatorActionsProps) {
  const { t } = useTranslation()
  const [confirm, setConfirm] = useState<'cancel' | 'stop' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const isRecurring = activity.isRecurring === true
  const creatorEditable = canEditActivity(activity)

  async function confirmAction() {
    if (!confirm) return
    setActionError(null)
    try {
      const ok =
        confirm === 'stop'
          ? await onStopRecurrence()
          : await onCancelActivity()
      if (ok) setConfirm(null)
    } catch (error) {
      setActionError(friendlyErrorKey(error))
    }
  }

  const activeDialog =
    confirm === 'cancel'
      ? {
          titleKey: isRecurring
            ? 'activityDetail.creatorActions.cancelRecurringTitle'
            : 'activityDetail.creatorActions.cancelTitle',
          bodyKey: isRecurring
            ? 'activityDetail.creatorActions.cancelRecurringBody'
            : 'activityDetail.creatorActions.cancelBody',
          confirmKey: isRecurring
            ? 'activityDetail.creatorActions.cancelRecurringConfirm'
            : 'activityDetail.creatorActions.cancelConfirm',
        }
      : confirm === 'stop'
        ? {
            titleKey: 'activityDetail.recurrence.stopTitle',
            bodyKey: 'activityDetail.recurrence.stopBody',
            confirmKey: 'activityDetail.recurrence.stopConfirm',
          }
        : null

  return (
    <section
      className="rounded-card bg-[#fff1ee] p-4"
      aria-label={t('activityDetail.creatorActions.title')}
    >
      <h2 className="font-head text-base font-extrabold text-deep">
        {t('activityDetail.creatorActions.title')}
      </h2>
      <p className="mt-1 text-xs leading-relaxed text-muted">
        {isRecurring
          ? t('activityDetail.creatorActions.subtitleRecurring')
          : t('activityDetail.creatorActions.subtitle')}
      </p>
      <Link
        to={`/chat/${encodeURIComponent(activity.id)}`}
        className="btn-primary mt-3 w-full"
      >
        {t('activityDetail.participation.ctaChat')}
      </Link>
      <div className="mt-3 flex flex-col gap-2">
        {creatorEditable ? (
          <Link
            to={`/activity-edit/${encodeURIComponent(activity.id)}`}
            className="btn-secondary w-full bg-white"
          >
            <KuniriIcons.pencilSimple
              size={20}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {t('activityDetail.creatorActions.edit')}
          </Link>
        ) : (
          <button
            type="button"
            className="btn-secondary w-full bg-white disabled:border-line disabled:text-muted disabled:opacity-100"
            disabled
          >
            <KuniriIcons.pencilSimple
              size={20}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {t('activityDetail.creatorActions.edit')}
          </button>
        )}
        <button
          type="button"
          className="btn-ghost w-full justify-start px-2 text-sm whitespace-normal text-danger hover:bg-[var(--danger-soft)] disabled:text-muted"
          disabled={!creatorEditable || cancelBusy}
          onClick={() => {
            setActionError(null)
            setConfirm('cancel')
          }}
        >
          <KuniriIcons.xCircle
            size={18}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {cancelBusy
            ? t('activityDetail.creatorActions.cancelling')
            : t(
                isRecurring
                  ? 'activityDetail.creatorActions.cancelRecurring'
                  : 'activityDetail.creatorActions.cancel',
              )}
        </button>
        {isRecurring ? (
          <button
            type="button"
            className="btn-secondary w-full bg-white"
            disabled={stopBusy || cancelBusy}
            onClick={() => {
              setActionError(null)
              setConfirm('stop')
            }}
          >
            <Repeat
              size={19}
              weight={kuniriIconWeight}
              aria-hidden="true"
            />
            {stopBusy
              ? t('activityDetail.recurrence.stopping')
              : t('activityDetail.recurrence.stop')}
          </button>
        ) : null}
        {!creatorEditable ? (
          <p className="text-xs leading-relaxed text-muted">
            {activity.status === 'cancelled'
              ? t('activityDetail.creatorActions.cancelled')
              : t('activityDetail.creatorActions.closed')}
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        open={activeDialog !== null}
        titleKey={
          activeDialog?.titleKey ?? 'activityDetail.creatorActions.cancelTitle'
        }
        bodyKey={
          activeDialog?.bodyKey ?? 'activityDetail.creatorActions.cancelBody'
        }
        confirmKey={
          activeDialog?.confirmKey ??
          'activityDetail.creatorActions.cancelConfirm'
        }
        cancelKey={
          confirm === 'stop'
            ? 'activityDetail.recurrence.stopCancel'
            : 'activityDetail.creatorActions.cancelCancel'
        }
        tone={confirm === 'cancel' ? 'danger' : 'default'}
        busy={confirm === 'stop' ? stopBusy : cancelBusy}
        onConfirm={() => void confirmAction()}
        onCancel={() => {
          if (!(confirm === 'stop' ? stopBusy : cancelBusy)) setConfirm(null)
        }}
      >
        {actionError ? (
          <p role="alert" className="text-sm font-semibold text-danger">
            {t(actionError)}
          </p>
        ) : null}
      </ConfirmDialog>
    </section>
  )
}
